// UI Helpers Layer subcomponent
import type {
  UIHelpersLayerAPI,
  TransformState,
  Point,
} from "../../../../types/stage-types.js";

// Import GridManager subcomponent
import {
  createGridManager,
  type GridManagerAPI,
  type GridConfig,
} from "./subcomponents/GridManager/GridManager.js";

// Import OriginTickManager subcomponent
import {
  createOriginTickManager,
  type OriginTickManagerAPI,
  type OriginTickConfig,
} from "./subcomponents/OriginTickManager/OriginTickManager.js";

// Import BoundaryManager subcomponent
import {
  createBoundaryManager,
  type BoundaryManagerAPI,
  type BoundaryConfig,
} from "./subcomponents/BoundaryManager/BoundaryManager.js";

// Import CursorTracker subcomponent
import {
  createCursorTracker,
  type CursorTrackerAPI,
  type CursorConfig,
} from "./subcomponents/CursorTracker/CursorTracker.js";

// Import HUDDisplay subcomponent
import {
  createHUDDisplay,
  type HUDDisplayAPI,
  type HUDConfig,
  type HUDData,
} from "./subcomponents/HUDDisplay/HUDDisplay.js";

// Import ElementHighlighter subcomponent
import {
  createElementHighlighter,
  type ElementHighlighterAPI,
  type ElementHighlighterConfig,
} from "./subcomponents/ElementHighlighter/ElementHighlighter.js";

export interface UIHelpersConfig {
  readonly gridSpacing?: number;
  readonly gridVisible?: boolean;
  readonly gridColor?: string;
  readonly showRulers?: boolean;
  readonly originTickVisible?: boolean;
  readonly productBoundaryVisible?: boolean;
  readonly linearGridVisible?: boolean;
  readonly linearGridSpacing?: number;
  readonly cursorTracking?: boolean;
  readonly hudVisible?: boolean;
  readonly elementHighlighting?: boolean;
  readonly elementHighlightDebug?: boolean;
}

/**
 * Creates a UI Helpers Layer for design aids (grid, rulers, guides)
 * @param container - Container element for the layer
 * @param config - Layer configuration
 * @returns UI Helpers Layer API
 */
export const createUIHelpersLayer = (
  container: HTMLElement,
  config: UIHelpersConfig = {}
): UIHelpersLayerAPI => {
  console.log("📐 Creating UI Helpers Layer");

  let isDestroyed = false;
  let isInitialized = false;

  // Stage state for rendering relative to transformed content
  let currentTransform: TransformState = {
    scale: 1,
    translateX: 0,
    translateY: 0,
    rotation: 0,
  };
  let stageBounds: DOMRect | null = null;

  // Interaction state - tracks if user is actively interacting (pan, zoom, etc.)
  let isInteracting = false;
  let interactionTimeout: number | null = null;
  let lastCursorPosition: Point | null = null;

  // GridManager subcomponent
  let gridManager: GridManagerAPI;

  // OriginTickManager subcomponent
  let originTickManager: OriginTickManagerAPI;

  // BoundaryManager subcomponent
  let boundaryManager: BoundaryManagerAPI;

  // CursorTracker subcomponent
  let cursorTracker: CursorTrackerAPI;

  // HUDDisplay subcomponent
  let hudDisplay: HUDDisplayAPI;

  // ElementHighlighter subcomponent
  let elementHighlighter: ElementHighlighterAPI;

  /**
   * Initialize the layer content
   */
  const initLayer = (): void => {
    // Clean container then initialize
    container.innerHTML = `
      <div class="ui-helpers__content">
      </div>
    `;

    // Initialize GridManager with configuration
    const gridConfig: GridConfig = {
      dotSize: 3,
      dotSpacing: config.gridSpacing || 18,
      lineSpacing: config.linearGridSpacing || 50,
      dotOpacity: 0.3,
      majorDotOpacity: 0.7,
      lineOpacity: 0.15,
      majorGridInterval: 50,
      widthOffset: 40,
      dotColor: "193, 183, 183",
      lineColor: "110, 100, 100",
    };

    gridManager = createGridManager(container, gridConfig);

    // Set initial grid visibility
    gridManager.setDotGridVisible(config.gridVisible ?? false);
    gridManager.setLinearGridVisible(config.linearGridVisible ?? false);

    // Initialize OriginTickManager with configuration
    const originTickConfig: OriginTickConfig = {
      visible: config.originTickVisible !== false, // Default to true
      size: 6, // Half of 12px total size
      color: "#ff0000", // Red for visibility
      lineWidth: 1,
    };

    originTickManager = createOriginTickManager(container, originTickConfig);

    // Initialize BoundaryManager with configuration
    const boundaryConfig: BoundaryConfig = {
      visible: config.productBoundaryVisible !== false, // Default to true
      label: "test-site",
      className: "ui-helpers__boundary",
      labelClassName: "ui-helpers__boundary-label",
    };

    boundaryManager = createBoundaryManager(container, boundaryConfig);

    // Initialize CursorTracker with configuration
    const cursorConfig: CursorConfig = {
      tracking: config.cursorTracking !== false, // Default to true
      container: container,
    };

    cursorTracker = createCursorTracker(container, cursorConfig);

    // Initialize HUDDisplay with configuration
    const hudConfig: HUDConfig = {
      visible: config.hudVisible === true, // Default to false
      container: container,
      className: "ui-helpers__info",
    };

    hudDisplay = createHUDDisplay(container, hudConfig);

    // Initialize ElementHighlighter with configuration
    const elementHighlighterConfig: ElementHighlighterConfig = {
      enabled: config.elementHighlighting !== false, // Default to true
      debugMode: config.elementHighlightDebug || false, // Debug mode off by default
    };

    elementHighlighter = createElementHighlighter(
      container,
      elementHighlighterConfig
    );

    // Initial render and HUD update
    render();
    updateHUD();
    isInitialized = true;
  };

  /**
   * Update HUD display with current state data
   */
  const updateHUD = (): void => {
    if (
      isDestroyed ||
      !gridManager ||
      !originTickManager ||
      !boundaryManager ||
      !cursorTracker ||
      !hudDisplay ||
      !elementHighlighter
    )
      return;

    const gridConfig = gridManager.getConfig();
    const gridState = gridManager.getState();
    const originTickState = originTickManager.getState();
    const boundaryState = boundaryManager.getState();
    const cursorState = cursorTracker.getState();

    // Calculate coordinate origin (use CursorTracker's method)
    const origin = cursorTracker.calculateOrigin();
    const originInStage = {
      x: origin.x * currentTransform.scale,
      y: origin.y * currentTransform.scale,
    };

    // Prepare HUD data
    const hudData: HUDData = {
      transform: currentTransform,
      stageBounds,
      origin,
      originInStage,
      cursor: {
        stagePosition: cursorState.stagePosition,
        productPosition: cursorState.productPosition,
        tracking: cursorState.tracking,
      },
      grid: {
        config: gridConfig,
        dotGridVisible: gridState.dotGridVisible,
        linearGridVisible: gridState.linearGridVisible,
      },
      originTick: {
        visible: originTickState.visible,
      },
      boundary: {
        visible: boundaryState.visible,
        label: boundaryState.label,
      },
    };

    // Update HUD display
    hudDisplay.update(hudData);
  };

  /**
   * Draw origin tick mark (delegates to OriginTickManager)
   * @param center - Center point for the tick mark (defaults to calculated origin)
   */
  const drawOriginTick = (center?: Point): void => {
    if (isDestroyed || !originTickManager) return;

    if (center) {
      originTickManager.drawAt(center);
    } else {
      originTickManager.render();
    }
  };

  /**
   * Set origin tick visibility (delegates to OriginTickManager)
   * @param visible - Whether to show the origin tick mark
   */
  const setOriginTickVisible = (visible: boolean): void => {
    if (isDestroyed || !originTickManager) return;

    originTickManager.setVisible(visible);
    updateHUD();
  };

  /**
   * Set product boundary visibility (delegates to BoundaryManager)
   * @param visible - Whether to show the product boundary
   */
  const setProductBoundaryVisible = (visible: boolean): void => {
    if (isDestroyed || !boundaryManager) return;

    boundaryManager.setVisible(visible);
    updateHUD();
  };

  /**
   * Update cursor position (delegates to CursorTracker)
   * @param position - Current cursor position in stage coordinates
   */
  const updateCursorPosition = (position: Point | null): void => {
    if (isDestroyed || !cursorTracker) return;

    cursorTracker.updatePosition(position);

    // Store the last cursor position for recalculation after interactions
    lastCursorPosition = position;

    // Update ElementHighlighter with current cursor position (only when not interacting)
    if (elementHighlighter && position && !isInteracting) {
      // Convert stage position to screen coordinates for elementFromPoint
      const stageRect = container.getBoundingClientRect();
      const screenX = stageRect.left + position.x;
      const screenY = stageRect.top + position.y;

      elementHighlighter.update(screenX, screenY, currentTransform.scale);
    }

    updateHUD(); // Refresh HUD with new cursor position
  };

  /**
   * Set grid visibility (delegates to GridManager)
   * @param visible - Whether grid should be visible
   */
  const setGridVisible = (visible: boolean): void => {
    if (isDestroyed || !gridManager) return;

    gridManager.setDotGridVisible(visible);
    updateHUD();
  };

  /**
   * Set grid spacing (delegates to GridManager)
   * @param spacing - Grid spacing in pixels
   */
  const setGridSpacing = (spacing: number): void => {
    if (isDestroyed || !gridManager) return;

    gridManager.setGridSpacing(spacing);
    updateHUD();
  };

  /**
   * Set linear grid visibility (delegates to GridManager)
   * @param visible - Whether to show the linear grid
   */
  const setLinearGridVisible = (visible: boolean): void => {
    if (isDestroyed || !gridManager) return;

    gridManager.setLinearGridVisible(visible);
    updateHUD();
  };

  /**
   * Set HUD visibility (delegates to HUDDisplay)
   * @param visible - Whether to show the HUD
   */
  const setHUDVisible = (visible: boolean): void => {
    if (isDestroyed || !hudDisplay) return;

    hudDisplay.setVisible(visible);
  };

  /**
   * Main render function that orchestrates all rendering
   */
  const render = (): void => {
    if (isDestroyed) return;

    // Let GridManager handle its own rendering
    if (gridManager) {
      gridManager.render();
    }

    // Let OriginTickManager handle its own rendering
    if (originTickManager) {
      originTickManager.render();
    }

    // Let BoundaryManager handle its own updates
    if (boundaryManager) {
      boundaryManager.update();
    }

    // CursorTracker and HUDDisplay don't need explicit rendering - they're state-only
  };

  /**
   * Handle transform changes
   * @param transform - New transform state
   */
  const setTransform = (transform: TransformState): void => {
    if (isDestroyed) return;

    currentTransform = { ...transform };

    // Update GridManager with new transform
    if (gridManager) {
      gridManager.updateTransform(transform);
    }

    // Update OriginTickManager with new transform
    if (originTickManager) {
      originTickManager.updateTransform(transform);
    }

    // Update CursorTracker with new transform
    if (cursorTracker) {
      cursorTracker.updateTransform(transform);
    }

    // BoundaryManager doesn't need transform updates - it calculates position dynamically

    // Re-render when transform changes
    render();
    updateHUD();
  };

  /**
   * Set layer visibility
   * @param visible - Visibility state
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    container.style.visibility = visible ? "visible" : "hidden";
  };

  /**
   * Handle container resize
   */
  const handleResize = (): void => {
    if (isDestroyed) return;

    // Re-render on resize
    render();
    updateHUD();
  };

  /**
   * Update stage state for rendering
   * @param transform - Current stage transform
   * @param bounds - Current stage bounds
   */
  const updateStageState = (
    transform: TransformState,
    bounds: DOMRect
  ): void => {
    if (isDestroyed) return;

    currentTransform = { ...transform };
    stageBounds = bounds;

    // Update GridManager with new transform
    if (gridManager) {
      gridManager.updateTransform(transform);
    }

    // Update OriginTickManager with new transform
    if (originTickManager) {
      originTickManager.updateTransform(transform);
    }

    // Update CursorTracker with new transform
    if (cursorTracker) {
      cursorTracker.updateTransform(transform);
    }

    // BoundaryManager doesn't need transform updates - it calculates position dynamically

    // Always update HUD display
    updateHUD();

    // Render when state changes
    render();
  };

  /**
   * Adjust grid configuration (delegates to GridManager)
   */
  const adjustGridConfig = (updates: Partial<GridConfig>): void => {
    if (isDestroyed || !gridManager) return;

    gridManager.adjustConfig(updates);
    updateHUD();
  };

  /**
   * Get current grid configuration (delegates to GridManager)
   */
  const getGridConfig = (): Readonly<GridConfig> | undefined => {
    if (isDestroyed || !gridManager) return undefined;

    return gridManager.getConfig();
  };

  /**
   * Reset grid configuration to defaults (delegates to GridManager)
   */
  const resetGridConfig = (): void => {
    if (isDestroyed || !gridManager) return;

    gridManager.resetConfig();
    updateHUD();
  };

  /**
   * Set interaction state (pan, zoom, drag operations)
   * @param interacting - Whether user is actively interacting
   */
  const setInteracting = (interacting: boolean): void => {
    if (isDestroyed) return;

    const highlightElement = document.querySelector(
      ".ui-helpers__element-highlight"
    ) as HTMLElement;

    if (interacting) {
      // Clear any existing timeout
      if (interactionTimeout) {
        clearTimeout(interactionTimeout);
        interactionTimeout = null;
      }

      // Set interacting immediately and hide highlight
      if (!isInteracting) {
        isInteracting = true;
        if (highlightElement) {
          highlightElement.style.display = "none";
        }
      }
    } else {
      // Debounce the end of interaction - only show highlight after a delay
      if (interactionTimeout) {
        clearTimeout(interactionTimeout);
      }

      interactionTimeout = window.setTimeout(() => {
        isInteracting = false;
        if (highlightElement) {
          highlightElement.style.display = "block";
        }

        // Recalculate highlight at current cursor position
        if (elementHighlighter && lastCursorPosition !== null) {
          const stageRect = container.getBoundingClientRect();
          const screenX = stageRect.left + lastCursorPosition.x;
          const screenY = stageRect.top + lastCursorPosition.y;
          elementHighlighter.update(screenX, screenY, currentTransform.scale);
        }

        interactionTimeout = null;
      }, 150); // 150ms delay to prevent flickering
    }
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Clear any pending interaction timeout
    if (interactionTimeout) {
      clearTimeout(interactionTimeout);
      interactionTimeout = null;
    }

    // Destroy GridManager
    if (gridManager) {
      gridManager.destroy();
    }

    // Destroy OriginTickManager
    if (originTickManager) {
      originTickManager.destroy();
    }

    // Destroy BoundaryManager
    if (boundaryManager) {
      boundaryManager.destroy();
    }

    // Destroy CursorTracker
    if (cursorTracker) {
      cursorTracker.destroy();
    }

    // Destroy HUDDisplay
    if (hudDisplay) {
      hudDisplay.destroy();
    }

    // Destroy ElementHighlighter
    if (elementHighlighter) {
      elementHighlighter.destroy();
    }

    container.innerHTML = "";
  };

  // Internal: destroy subcomponents without destroying the layer instance
  const cleanupSubcomponents = (): void => {
    if (interactionTimeout) {
      clearTimeout(interactionTimeout);
      interactionTimeout = null;
    }
    try {
      gridManager?.destroy?.();
    } catch {}
    try {
      originTickManager?.destroy?.();
    } catch {}
    try {
      boundaryManager?.destroy?.();
    } catch {}
    try {
      cursorTracker?.destroy?.();
    } catch {}
    try {
      hudDisplay?.destroy?.();
    } catch {}
    try {
      elementHighlighter?.destroy?.();
    } catch {}
    // Reset references
    // @ts-expect-error: re-init later
    gridManager = undefined;
    // @ts-expect-error: re-init later
    originTickManager = undefined;
    // @ts-expect-error: re-init later
    boundaryManager = undefined;
    // @ts-expect-error: re-init later
    cursorTracker = undefined;
    // @ts-expect-error: re-init later
    hudDisplay = undefined;
    // @ts-expect-error: re-init later
    elementHighlighter = undefined;
    isInitialized = false;
    container.innerHTML = "";
  };

  // Public: reset helpers depending on mode
  const resetForMode = (mode: "edit" | "sketch"): void => {
    if (isDestroyed) return;
    // If not initialized yet and switching to edit, just init
    if (!isInitialized && mode === "edit") {
      initLayer();
      return;
    }
    cleanupSubcomponents();
    if (mode === "edit") {
      initLayer();
    } else {
      // sketch → leave layer empty
      container.innerHTML = "";
    }
  };

  // Initialize layer
  initLayer();

  // Return API
  return {
    setGridVisible,
    setGridSpacing,
    render,
    setTransform,
    setVisible,
    destroy,
    resetForMode,
    // Additional helper-specific methods
    handleResize,
    updateStageState,
    // Origin tick methods (now delegated to OriginTickManager)
    drawOriginTick,
    setOriginTickVisible,
    updateCursorPosition, // Now delegated to CursorTracker
    // Product boundary methods (now delegated to BoundaryManager)
    setProductBoundaryVisible,
    // Linear grid methods (now delegated to GridManager)
    setLinearGridVisible,
    // HUD visibility methods (now delegated to HUDDisplay)
    setHUDVisible,
    // Grid configuration methods (now delegated to GridManager)
    adjustGridConfig,
    getGridConfig,
    resetGridConfig,
    // Interaction state management
    setInteracting,
  };
};
